import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FolderOpen,
  Layers,
  MoreHorizontal,
  Pencil,
  Search,
  Star,
  Trash2,
  Upload,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { DocumentFormDialog } from "@/components/DocumentFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDeleteDocument } from "@/hooks/useDocumentMutations";
import type { DocumentRow } from "@/lib/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDocuments } from "@/hooks/useDocuments";
import { FILE_TYPE_STYLES } from "@/lib/file-icons";
import { formatBytes, formatDate } from "@/lib/format";
import type { FileType, WorkflowState } from "@/lib/types";
import { cn } from "@/lib/utils";

const FILE_TYPES: { value: FileType | ""; label: string }[] = [
  { value: "",         label: "All types" },
  { value: "document", label: "Documents" },
  { value: "video",    label: "Videos" },
  { value: "audio",    label: "Audio" },
  { value: "image",    label: "Images" },
  { value: "other",    label: "Other" },
];

// Shared library never shows "Only me" rows — those live on /private.
const WORKFLOW_STATES: { value: WorkflowState | ""; label: string }[] = [
  { value: "",                   label: "Any state" },
  { value: "published",          label: "Published" },
  { value: "private_only",       label: "Staff portal" },
  { value: "pending_review",     label: "Pending director" },
  { value: "pending_ceo_review", label: "Pending CEO" },
];

const PAGE_SIZE = 20;

export default function DocumentLibrary() {
  const [search,        setSearch]        = useState("");
  const [searchDraft,   setSearchDraft]   = useState("");
  const [fileType,      setFileType]      = useState<FileType | "">("");
  const [workflowState, setWorkflowState] = useState<WorkflowState | "">("");
  const [scope,         setScope]         = useState<"my" | "all">("all");
  const [page,          setPage]          = useState(1);
  const [uploadOpen,    setUploadOpen]    = useState(false);
  const [editing,       setEditing]       = useState<DocumentRow | null>(null);

  const { data, isLoading, isFetching } = useDocuments({
    search:        search || undefined,
    fileType:      fileType || undefined,
    workflowState: workflowState || undefined,
    scope,
    audience:      "shared",        // /library is the Shared library — owner_only rows live on /private
    page,
  });

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* ---------- Header ---------- */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Shared library</h1>
          <p className="text-sm text-muted-foreground">
            Resources shared with the staff portal or out to the public site.
            For files only you can see, head to <Link to="/private" className="text-primary hover:underline">Private vault</Link>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <a href="/api/v1/documents/export.csv" download>
              Export CSV
            </a>
          </Button>
          <Button onClick={() => setUploadOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Upload resource
          </Button>
        </div>
      </header>

      <DocumentFormDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
      <DocumentFormDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        document={editing}
      />

      {/* ---------- Filter bar ---------- */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form onSubmit={onSearchSubmit} className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by title or description…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              className="h-10 pl-9 pr-3"
            />
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              value={fileType}
              onChange={(v) => { setFileType(v as FileType | ""); setPage(1); }}
              options={FILE_TYPES}
            />
            <FilterSelect
              value={workflowState}
              onChange={(v) => { setWorkflowState(v as WorkflowState | ""); setPage(1); }}
              options={WORKFLOW_STATES}
            />
            <FilterSelect
              value={scope}
              onChange={(v) => { setScope(v as "my" | "all"); setPage(1); }}
              options={[
                { value: "all", label: "All visible" },
                { value: "my",  label: "Mine only" },
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {/* ---------- Table ---------- */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Document</TableHead>
                <TableHead className="hidden lg:table-cell">Owner</TableHead>
                <TableHead className="hidden sm:table-cell">State</TableHead>
                <TableHead className="hidden lg:table-cell">Size</TableHead>
                <TableHead className="hidden xl:table-cell">Updated</TableHead>
                <TableHead className="w-16 text-right pr-5" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6} className="py-4">
                      <Skeleton className="h-10 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : data && data.results.length > 0 ? (
                data.results.map((doc) => {
                  const style = FILE_TYPE_STYLES[doc.file_type];
                  const Icon  = style.icon;
                  return (
                    <TableRow key={doc.id} className="border-border/60">
                      {/* Document cell — icon + title + extension. */}
                      <TableCell className="py-3 pl-5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                            style.chipClass,
                          )}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              {doc.is_featured && (
                                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                              )}
                              <Link
                                to={`/library/${doc.id}`}
                                className="truncate text-sm font-medium hover:text-primary hover:underline"
                              >
                                {doc.title}
                              </Link>
                              {/* Multi-file pip: only renders when the
                                  resource bundles >1 file. Layered icon
                                  + count reads as "this is a bundle". */}
                              {doc.file_count > 1 && (
                                <span
                                  className="inline-flex items-center gap-0.5 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary"
                                  title={`Bundle of ${doc.file_count} files`}
                                >
                                  <Layers className="h-3 w-3" />
                                  {doc.file_count}
                                </span>
                              )}
                            </div>
                            <div className="text-xs font-mono uppercase text-muted-foreground">
                              {doc.file_extension || doc.file_type}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                        {doc.owner_name || "—"}
                      </TableCell>

                      <TableCell className="hidden sm:table-cell">
                        <WorkflowBadge state={doc.workflow_state} label={doc.workflow_state_display} />
                      </TableCell>

                      <TableCell className="hidden lg:table-cell text-sm font-mono tabular-nums text-muted-foreground">
                        {formatBytes(doc.file_size_bytes)}
                      </TableCell>

                      <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">
                        {formatDate(doc.updated_at)}
                      </TableCell>

                      <TableCell className="pr-5 text-right">
                        <RowActions doc={doc} onEdit={() => setEditing(doc)} />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-16">
                    <div className="text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <FolderOpen className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <h3 className="text-sm font-semibold">No documents match</h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Try clearing a filter or broadening your search.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ---------- Pagination ---------- */}
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          {data ? (
            <>
              <span className="font-mono font-semibold tabular-nums">
                {data.count.toLocaleString()}
              </span>{" "}
              document{data.count === 1 ? "" : "s"}
              {isFetching && <span className="ml-2 text-xs">loading…</span>}
            </>
          ) : "—"}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            disabled={!data?.previous}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
            {page} / {totalPages}
          </div>
          <Button
            variant="outline" size="sm"
            disabled={!data?.next}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface FilterSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}

function FilterSelect({ value, onChange, options }: FilterSelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function WorkflowBadge({ state, label }: { state: WorkflowState; label: string }) {
  const className =
    state === "published"          ? "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30"
  : state === "pending_review"     ? "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30"
  : state === "pending_ceo_review" ? "bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30"
  : state === "rejected"           ? "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30"
  : state === "private_only"       ? "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30"
                                   : "bg-muted/40 text-foreground/80 border-border";
  return (
    <Badge variant="outline" className={cn("border font-medium", className)}>
      {label}
    </Badge>
  );
}

/**
 * Per-row action menu — Open / Edit / Delete.
 *
 * Visibility rules mirror `apps.private.utils.can_manage_resource`:
 *   - Admin   → always.
 *   - Director → cross-dept (we show the menu; backend rejects out-of-scope).
 *   - Owner   → yes UNLESS workflow_state == "published" (published rows
 *               lock the owner out so only directors can take them down).
 *   - Others  → menu hidden, just an Open link if there's a file URL.
 *
 * The backend re-checks every action, so being too permissive here just
 * means a 403 from the API — no silent privilege escalation.
 */
function RowActions({
  doc, onEdit,
}: {
  doc: DocumentRow;
  onEdit: () => void;
}) {
  const { data: user } = useCurrentUser();
  const del = useDeleteDocument();
  const confirm = useConfirm();

  const canManage = !!user && (
    user.is_director_level
    || (doc.owner_id === user.id && doc.workflow_state !== "published")
  );

  async function onDelete() {
    const ok = await confirm({
      title: `Delete "${doc.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(doc.id);
  }

  if (!canManage) {
    // Read-only viewers just get an Open button (when there's a file).
    return doc.file_url ? (
      <Button asChild variant="ghost" size="icon" className="h-8 w-8">
        <a href={doc.file_url} target="_blank" rel="noreferrer">
          <ExternalLink className="h-4 w-4" />
          <span className="sr-only">Open</span>
        </a>
      </Button>
    ) : null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {doc.file_url && (
          <DropdownMenuItem asChild>
            <a href={doc.file_url} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />Open
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          disabled={del.isPending}
          onSelect={(e) => { e.preventDefault(); onDelete(); }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {del.isPending ? "Deleting…" : "Delete"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
